import { Button, Input } from '@ohear/ui'
import { useState } from 'react'

/**
 * SSN entry field with two modes:
 *
 *  - Display mode: when an SSN is already on file (caller passes `last4`)
 *    we render `●●●-●●-1234` and an "Update" button. Clicking switches to
 *    edit mode so the actor must explicitly request a re-entry.
 *  - Edit mode: a single text input that auto-formats as the actor types,
 *    plus a confirm/cancel pair. The `onSubmit` callback receives the bare
 *    9-digit string; the parent is responsible for calling the API and
 *    flipping `last4`.
 *
 * No visual "warning red" — we follow the friendly_ui_tone rule and show
 * blocking states with text help, not a screaming color.
 */
export interface SsnInputProps {
  /** Last 4 of an SSN already on file, or null for first-time entry. */
  last4: string | null
  /** Optional title — e.g. "Social Security Number" or a translated string. */
  label: string
  /** Helper text shown beneath the field. */
  hint?: string
  /** Submit button text in edit mode. */
  submitLabel: string
  /** "Update" button text in display mode. */
  updateLabel: string
  /** "Cancel" button text in edit mode. */
  cancelLabel: string
  /** Called with the cleaned 9-digit SSN. */
  onSubmit: (ssn: string) => Promise<void> | void
  /** True while the parent's mutation is in flight. */
  busy?: boolean
}

function formatSsn(input: string): string {
  const onlyDigits = input.replace(/\D/g, '').slice(0, 9)
  if (onlyDigits.length <= 3) return onlyDigits
  if (onlyDigits.length <= 5) return `${onlyDigits.slice(0, 3)}-${onlyDigits.slice(3)}`
  return `${onlyDigits.slice(0, 3)}-${onlyDigits.slice(3, 5)}-${onlyDigits.slice(5)}`
}

export function SsnInput({
  last4,
  label,
  hint,
  submitLabel,
  updateLabel,
  cancelLabel,
  onSubmit,
  busy = false,
}: SsnInputProps) {
  const [mode, setMode] = useState<'display' | 'edit'>(last4 ? 'display' : 'edit')
  const [draft, setDraft] = useState('')
  const [error, setError] = useState<string | null>(null)

  if (mode === 'display' && last4) {
    return (
      <div className="flex items-center gap-3">
        <span className="font-mono text-[14px]">{`●●●-●●-${last4}`}</span>
        <Button variant="ghost" onClick={() => setMode('edit')}>
          {updateLabel}
        </Button>
      </div>
    )
  }

  const cleaned = draft.replace(/\D/g, '')
  const valid = cleaned.length === 9

  return (
    <div className="flex flex-col gap-2">
      <label className="flex flex-col gap-1 text-[13px]">
        <span className="font-medium">{label}</span>
        <Input
          value={draft}
          onChange={(e) => {
            setDraft(formatSsn(e.target.value))
            setError(null)
          }}
          placeholder="123-45-6789"
          inputMode="numeric"
          autoComplete="off"
          maxLength={11}
          disabled={busy}
          aria-describedby="ssn-input-hint"
        />
        {hint ? (
          <span id="ssn-input-hint" className="text-[12px] text-[var(--text-2)]">
            {hint}
          </span>
        ) : null}
        {error ? <span className="text-[12px] text-[var(--text-2)]">{error}</span> : null}
      </label>
      <div className="flex items-center gap-2">
        <Button
          variant="primary"
          disabled={!valid || busy}
          loading={busy}
          onClick={async () => {
            try {
              await onSubmit(cleaned)
              setDraft('')
              if (last4) setMode('display')
            } catch (e) {
              setError(e instanceof Error ? e.message : 'Could not save')
            }
          }}
        >
          {submitLabel}
        </Button>
        {last4 ? (
          <Button
            variant="ghost"
            disabled={busy}
            onClick={() => {
              setDraft('')
              setError(null)
              setMode('display')
            }}
          >
            {cancelLabel}
          </Button>
        ) : null}
      </div>
    </div>
  )
}
